import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Heart, Users, MessageCircle, Star, Sparkles, Stars,
  Smartphone, Download, Check, Globe, X, Apple, Play,
  Search, UserCheck, Bell, Lock
} from "lucide-react";
import { Button } from "../components/ui/button";
import { FooterSection } from "../components/landing/FooterSection";
import { toast } from "sonner";

// 정식 출시된 Coupling 앱 스토어 URL (랜딩페이지와 동일)
const APP_STORE_URL = "https://apps.apple.com/il/app/%EC%BB%A4%ED%94%8C%EB%A7%81-coupling/id6752711029";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.liz.coupling.apps2&hl=ko";

export default function DatingAppPage() {
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  // Animation variants (matching CouplingAppSection)
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const floatingVariants = {
    animate: {
      y: [-10, 10, -10],
      transition: { duration: 4, repeat: Infinity, ease: "easeInOut" }
    }
  };

  const features = [
    { 
      icon: Search, 
      title: "스마트 매칭", 
      desc: "AI 기반 맞춤형 매칭 시스템으로 취향과 성향이 맞는 이성을 추천합니다.",
      color: "bg-burgundy-500"
    },
    { 
      icon: UserCheck, 
      title: "검증된 회원", 
      desc: "본인 인증과 프로필 심사를 통해 신뢰할 수 있는 회원만 활동합니다.",
      color: "bg-gold-600"
    },
    { 
      icon: MessageCircle, 
      title: "실시간 채팅", 
      desc: "매칭된 상대와 실시간으로 대화하며 서로를 알아갈 수 있습니다.",
      color: "bg-burgundy-600"
    },
    { 
      icon: Bell, 
      title: "알림 서비스", 
      desc: "새로운 매칭, 메시지, 좋아요 등을 실시간 알림으로 받아보세요.",
      color: "bg-gold-500"
    },
    { 
      icon: Lock, 
      title: "프라이버시 보호", 
      desc: "개인정보 보호와 안전한 데이팅 환경을 최우선으로 합니다.",
      color: "bg-burgundy-500"
    },
    { 
      icon: Globe, 
      title: "글로벌 네트워크", 
      desc: "전 세계 한인들과 연결되어 국경을 초월한 인연을 만나보세요.",
      color: "bg-gold-600"
    },
  ];

  const stats = [
    { value: "50K+", label: "활성 회원" },
    { value: "40%", label: "매칭 성공률" },
    { value: "4.8", label: "앱스토어 평점" },
    { value: "100K+", label: "성공 커플" },
  ];

  const howItWorks = [
    { step: "01", title: "회원가입", desc: "간단한 가입 절차로 시작하세요", icon: Smartphone },
    { step: "02", title: "프로필 작성", desc: "매력적인 프로필을 완성하세요", icon: UserCheck },
    { step: "03", title: "매칭 시작", desc: "AI가 추천하는 이성을 만나보세요", icon: Heart },
    { step: "04", title: "대화하기", desc: "마음에 드는 분과 대화를 시작하세요", icon: MessageCircle },
  ];

  const membershipBenefits = [
    { plan: "일반회원", benefits: ["기본 프로필 열람", "하루 5회 좋아요", "기본 매칭"] },
    { plan: "정회원", benefits: ["무제한 프로필 열람", "무제한 좋아요", "고급 매칭 필터", "프로필 상위 노출"] },
    { plan: "프리미엄", benefits: ["모든 정회원 혜택", "슈퍼 좋아요 무제한", "읽음 확인 기능", "프로필 부스트"] },
  ];

  return (
    <div className="min-h-screen bg-stone-200" data-testid="dating-app-page">
      {/* Hero Section - Matching CouplingAppSection style */}
      <section className="pt-24 pb-16 lg:pb-24 bg-stone-200 relative overflow-hidden">
        <div className="max-w-[95%] mx-auto">
          {/* Box Container with Gray Background - Same as CouplingAppSection */}
          <div className="bg-stone-100 rounded-3xl p-4 md:p-6 shadow-lg">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center"
            >
              {/* Left Content - Text Section */}
              <div className="lg:col-span-5 order-2 lg:order-1 text-center z-10">
                <motion.div variants={itemVariants} className="mb-6">
                  <span className="text-gold-500 text-sm font-medium tracking-wider uppercase">
                    Dating App
                  </span>
                </motion.div>

                <motion.h2
                  variants={itemVariants}
                  className="font-sans text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
                >
                  <span className="text-stone-800">Find Your</span>
                  <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-burgundy-500 via-burgundy-600 to-burgundy-500">
                    Coupling
                  </span>
                </motion.h2>

                <motion.p
                  variants={itemVariants}
                  className="text-stone-600 text-lg md:text-xl mb-6 leading-relaxed max-w-md mx-auto"
                >
                  신뢰와 진정성을 추구하는 사람들을 위한
                  <span className="block text-stone-800 font-semibold mt-1">고품격 프리미엄 매칭 앱</span>
                </motion.p>

                <motion.div
                  variants={itemVariants}
                  className="relative mb-8 py-4 px-6 bg-stone-50 rounded-2xl border border-stone-200 max-w-md mx-auto"
                >
                  <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-1 h-12 bg-gradient-to-b from-gold-500 to-burgundy-500 rounded-full" />
                  <p className="text-burgundy-600 font-medium text-lg md:text-xl italic">
                    "싱글링보다 커플링,
                    <br />
                    웨딩링보다 커플링"
                  </p>
                </motion.div>

                {/* CTA Buttons */}
                <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    onClick={() => setShowDownloadModal(true)}
                    className="group relative px-8 py-6 bg-gradient-to-r from-burgundy-500 to-burgundy-600 hover:from-burgundy-600 hover:to-burgundy-700 text-white font-semibold text-base rounded-full shadow-lg shadow-burgundy-500/25 hover:shadow-xl hover:shadow-burgundy-500/30 transition-all duration-300 flex items-center justify-center gap-3"
                    data-testid="dating-app-download-btn"
                  >
                    <Download className="w-5 h-5 group-hover:animate-bounce" />
                    앱 다운로드
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={() => {
                      const target = document.getElementById("features-section");
                      if (target) {
                        target.scrollIntoView({ behavior: "smooth", block: "start" });
                      }
                    }}
                    className="px-8 py-6 border-2 border-stone-300 bg-white hover:bg-stone-50 text-stone-700 font-medium text-base rounded-full transition-all duration-300"
                    data-testid="dating-app-learn-more-btn"
                  >
                    더 알아보기
                  </Button>
                </motion.div>

                {/* Platform badges */}
                <motion.div variants={itemVariants} className="mt-8 flex items-center gap-6 justify-center">
                  <div className="flex items-center gap-2 text-stone-500 text-sm">
                    <Apple className="w-5 h-5" />
                    <span>iOS</span>
                  </div>
                  <div className="w-px h-4 bg-stone-300" />
                  <div className="flex items-center gap-2 text-stone-500 text-sm">
                    <Play className="w-5 h-5" />
                    <span>Android</span>
                  </div>
                  <div className="w-px h-4 bg-stone-300" />
                  <span className="text-xs text-stone-400 uppercase tracking-wider">Coming Soon</span>
                </motion.div>

                {/* Beta Notice */}
                <motion.p
                  variants={itemVariants}
                  className="mt-4 text-xs text-stone-400"
                  data-testid="dating-app-beta-notice"
                >
                  현재 베타 버전으로 운영됩니다
                </motion.p>
              </div>

              {/* Right Content - Hero Image */}
              <motion.div
                variants={itemVariants}
                className="lg:col-span-7 order-1 lg:order-2 relative"
              >
                {/* Main Image Container */}
                <div className="relative mx-auto max-w-2xl">
                  {/* Glow effect behind image */}
                  <div className="absolute inset-0 bg-gradient-to-r from-burgundy-200/40 via-gold-200/30 to-burgundy-200/40 rounded-[2rem] blur-3xl transform scale-95" />
                  
                  {/* Image frame with rings decoration */}
                  <div className="relative">
                    {/* Decorative ring frame */}
                    <div className="absolute -inset-4 border-2 border-burgundy-200/40 rounded-[2.5rem] transform rotate-2" />
                    <div className="absolute -inset-8 border border-gold-300/30 rounded-[3rem] transform -rotate-1" />
                    
                    {/* Main image */}
                    <div className="relative rounded-[2rem] overflow-hidden shadow-2xl shadow-stone-300/50">
                      <img
                        src="https://customer-assets.emergentagent.com/job_62e113a6-fe02-48dd-a8bb-4b26ed6daf98/artifacts/p1wq8on5_ChatGPT%20Image%20Mar%2023%2C%202026%2C%2011_28_50%20PM.png"
                        alt="Coupling App - 커플링 매칭"
                        className="w-full h-auto object-cover"
                        data-testid="dating-app-hero-image"
                      />
                      
                      {/* Subtle overlay for depth */}
                      <div className="absolute inset-0 bg-gradient-to-t from-stone-900/20 via-transparent to-transparent" />
                    </div>

                    {/* Floating stats cards */}
                    <motion.div
                      variants={floatingVariants}
                      animate="animate"
                      className="absolute -left-4 lg:-left-8 top-1/4 bg-white rounded-2xl p-4 shadow-xl border border-stone-100"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-burgundy-500 to-burgundy-600 rounded-full flex items-center justify-center">
                          <Heart className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-stone-500">매칭 성공률</p>
                          <p className="text-lg font-bold text-stone-800">40%</p>
                        </div>
                      </div>
                    </motion.div>

                    <motion.div
                      variants={floatingVariants}
                      animate="animate"
                      style={{ animationDelay: "2s" }}
                      className="absolute -right-4 lg:-right-8 bottom-1/4 bg-white rounded-2xl p-4 shadow-xl border border-stone-100"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full overflow-hidden">
                          <img src="https://customer-assets.emergentagent.com/job_9277ad9a-6109-40e4-9d7a-82032fc09839/artifacts/mcdiksg4_image.png" alt="" className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <p className="text-xs text-stone-500">프리미엄 회원</p>
                          <p className="text-lg font-bold text-stone-800">1,000</p>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-stone-100 border-b border-stone-200">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <p className="text-3xl md:text-4xl font-bold text-burgundy-600">{stat.value}</p>
                <p className="text-stone-500 mt-1">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features-section" className="py-16 bg-stone-200">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-gold-500 font-medium text-sm tracking-wider uppercase">Features</span>
            <h2 className="text-3xl md:text-4xl font-sans font-[720] text-stone-800 mt-2">
              앱의 주요 기능
            </h2>
            <p className="text-stone-500 mt-4 max-w-2xl mx-auto">
              Coupling 앱만의 특별한 기능으로 진정한 인연을 찾아보세요
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-stone-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow border border-stone-200"
              >
                <div className={`w-14 h-14 ${feature.color} rounded-2xl flex items-center justify-center mb-4`}>
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-bold text-stone-800 text-lg mb-2">{feature.title}</h3>
                <p className="text-stone-500 text-sm">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-stone-100">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-gold-500 font-medium text-sm tracking-wider uppercase">How It Works</span>
            <h2 className="text-3xl md:text-4xl font-sans font-[720] text-stone-800 mt-2">
              이용 방법
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {howItWorks.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center relative"
              >
                {/* Connector Line */}
                {index < howItWorks.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-burgundy-200" />
                )}
                
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-burgundy-500 to-burgundy-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <step.icon className="w-8 h-8 text-white" />
                  </div>
                  <span className="text-xs text-gold-500 font-bold">STEP {step.step}</span>
                  <h3 className="font-bold text-stone-800 text-lg mt-1 mb-2">{step.title}</h3>
                  <p className="text-stone-500 text-sm">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Membership Benefits */}
      <section className="py-16 bg-gradient-to-br from-stone-100 to-stone-200">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-gold-500 font-medium text-sm tracking-wider uppercase">Membership</span>
            <h2 className="text-3xl md:text-4xl font-sans font-[720] text-stone-800 mt-2">
              회원 등급별 혜택
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {membershipBenefits.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`bg-stone-50 rounded-2xl p-6 border ${index === 2 ? 'ring-2 ring-burgundy-500 border-burgundy-200' : 'border-stone-200'}`}
              >
                {index === 2 && (
                  <span className="inline-block px-3 py-1 bg-burgundy-500 text-white text-xs font-bold rounded-full mb-4">추천</span>
                )}
                <h3 className="font-bold text-stone-800 text-xl mb-4">{item.plan}</h3>
                <ul className="space-y-3">
                  {item.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-center gap-2 text-stone-600">
                      <Check className="w-5 h-5 text-burgundy-500" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-stone-600 to-stone-700">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Heart className="w-12 h-12 text-stone-300 mx-auto mb-4" fill="currentColor" />
            <h2 className="text-3xl md:text-4xl font-sans font-[720] text-white mb-4">
              지금 바로 시작하세요
            </h2>
            <p className="text-stone-300 text-lg mb-8">
              당신의 인연이 기다리고 있습니다
            </p>
            <Button
              onClick={() => setShowDownloadModal(true)}
              className="bg-stone-200 hover:bg-white text-stone-800 px-8 py-6 text-lg font-bold rounded-full shadow-lg"
            >
              <Download className="w-5 h-5 mr-2" />
              앱 다운로드
            </Button>
          </motion.div>
        </div>
      </section>

      <FooterSection />

      {/* Download Modal - Matching CouplingAppSection style */}
      <AnimatePresence>
        {showDownloadModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/80 backdrop-blur-md p-4"
            onClick={() => setShowDownloadModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="bg-white rounded-[2rem] shadow-2xl max-w-md w-full overflow-hidden border border-stone-200"
              onClick={(e) => e.stopPropagation()}
              data-testid="dating-app-download-modal"
            >
              {/* Modal Header */}
              <div className="relative p-8 text-center">
                <button
                  onClick={() => setShowDownloadModal(false)}
                  className="absolute top-4 right-4 p-2 hover:bg-stone-100 rounded-full transition-colors text-stone-400 hover:text-stone-700"
                  data-testid="close-dating-app-download-modal"
                >
                  <X className="w-5 h-5" />
                </button>
                
                <div className="w-20 h-20 mx-auto rounded-2xl overflow-hidden mb-4 shadow-lg">
                  <img src="https://customer-assets.emergentagent.com/job_9277ad9a-6109-40e4-9d7a-82032fc09839/artifacts/mcdiksg4_image.png" alt="Coupling" className="w-full h-full object-contain" />
                </div>
                <h3 className="text-2xl font-bold text-stone-800 mb-1">Coupling</h3>
                <p className="text-burgundy-500 text-sm font-medium">Premium Dating App</p>
              </div>

              {/* Modal Body */}
              <div className="px-8 pb-8">
                <p className="text-stone-500 text-center mb-8">
                  Coupling 앱을 다운로드하고<br />
                  <span className="font-semibold text-stone-800">소중한 인연을 만나보세요!</span>
                </p>

                {/* Download Options */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {/* App Store */}
                  <a
                    href={APP_STORE_URL || "#"}
                    target={APP_STORE_URL ? "_blank" : undefined}
                    rel={APP_STORE_URL ? "noopener noreferrer" : undefined}
                    onClick={(e) => {
                      if (!APP_STORE_URL) {
                        e.preventDefault();
                        toast.info("iOS 앱은 곧 출시됩니다 🍎", {
                          description: "회원가입하시면 출시 시 가장 먼저 알림 받으실 수 있어요.",
                        });
                      }
                    }}
                    className="group p-5 bg-stone-800 hover:bg-stone-900 border border-stone-700 text-white rounded-2xl transition-all duration-300 flex flex-col items-center justify-center cursor-pointer"
                    data-testid="dating-app-download-ios"
                  >
                    <Apple className="w-10 h-10 mb-3 text-white/80 group-hover:text-white transition-colors" />
                    <p className="text-xs text-stone-400">Download on</p>
                    <p className="text-sm font-bold">App Store</p>
                  </a>

                  {/* Google Play */}
                  <a
                    href={PLAY_STORE_URL || "#"}
                    target={PLAY_STORE_URL ? "_blank" : undefined}
                    rel={PLAY_STORE_URL ? "noopener noreferrer" : undefined}
                    onClick={(e) => {
                      if (!PLAY_STORE_URL) {
                        e.preventDefault();
                        toast.info("Android 앱은 곧 출시됩니다 🤖", {
                          description: "회원가입하시면 출시 시 가장 먼저 알림 받으실 수 있어요.",
                        });
                      }
                    }}
                    className="group p-5 bg-stone-800 hover:bg-stone-900 border border-stone-700 text-white rounded-2xl transition-all duration-300 flex flex-col items-center justify-center cursor-pointer"
                    data-testid="dating-app-download-android"
                  >
                    <Play className="w-10 h-10 mb-3 text-white/80 group-hover:text-white transition-colors" />
                    <p className="text-xs text-stone-400">GET IT ON</p>
                    <p className="text-sm font-bold">Google Play</p>
                  </a>
                </div>

                {/* QR Code Section */}
                <div className="pt-6 border-t border-stone-200">
                  <p className="text-center text-stone-400 text-sm mb-4">
                    QR 코드를 스캔하세요
                  </p>
                  <div className="flex justify-center">
                    <div className="w-28 h-28 bg-stone-50 border border-stone-200 rounded-xl flex items-center justify-center">
                      <div className="text-center">
                        <Smartphone className="w-8 h-8 text-stone-400 mx-auto mb-1" />
                        <p className="text-xs text-stone-500">QR Code</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Coming Soon Notice */}
                <div className="mt-6 flex items-center justify-center gap-2">
                  <div className="w-2 h-2 bg-burgundy-400 rounded-full animate-pulse" />
                  <p className="text-burgundy-500 text-sm font-medium">
                    앱 출시 예정 - Coming Soon
                  </p>
                </div>

                {/* Beta Notice */}
                <p className="mt-3 text-center text-xs text-stone-400" data-testid="dating-app-modal-beta-notice">
                  현재 베타 버전으로 운영됩니다
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
